import { CloseRounded } from '@mui/icons-material'
import { ExpandMoreRounded } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { StripeConnectButton } from '../Adopters/StripeConnectButton'
import paymentsFlexImg from './assets/payments-flex.svg'
import paymentsFastImg from './assets/payments-fast.svg'
import paymentsTrackImg from './assets/payments-track.svg'

type Props = {
  open: boolean
  onClose: () => void
}

const SectionTitle: React.FC<{ index: number; title: string }> = ({ index, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        border: '3px solid #FCB402',
        color: '#FCB402',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 13,
        flexShrink: 0,
      }}
    >
      {index}
    </Box>
    <Typography sx={{ fontWeight: 800, color: '#171F34', fontSize: 16, lineHeight: 1.2 }}>{title}</Typography>
  </Box>
)

export const PaymentsIntroModal: React.FC<Props> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ position: 'relative', pr: 6, py: 2.5, textAlign: 'center' }}>
        <Stack spacing={0.75} sx={{ alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#7A8197' }}>Gratuit</Typography>
          <Typography sx={{ fontSize: '1.7rem', fontWeight: 900, color: '#171F34', lineHeight: 1.15 }}>
            Faites-vous payer grâce à un lien.
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#171F34', lineHeight: 1.2 }}>
            0% de commission a la charge.
          </Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 10, right: 10 }}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2.25 }}>
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            mb: 2.5,
            borderRadius: 3,
            border: '1px solid #F0DE9D',
            backgroundColor: '#FFF5D6',
            overflow: 'hidden',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            sx={{
              px: 2,
              py: 0.75,
              backgroundColor: '#FFF5D6',
              '& .MuiAccordionSummary-content': { my: 0.75 },
            }}
          >
            <Typography sx={{ fontWeight: 900, color: '#171F34' }}>Comment ça marche ?</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, backgroundColor: 'white' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                gap: { xs: 2.25, md: 3 },
              }}
            >
              <Box>
                <SectionTitle index={1} title="Activer votre compte Stripe connect x Petso" />
                <Typography sx={{ color: '#4B5566', mb: 0, fontSize: { xs: 13, md: 14 } }}>
                  Stripe est un partenaire technique spécialisé et agréé pour assurer la gestion des paiements et
                  permettre la réception des frais d&apos;adoption en ligne de manière fiable et sécurisée.
                </Typography>
              </Box>

              <Box>
                <SectionTitle index={2} title="Comment ?" />
                <Typography sx={{ color: '#4B5566', mb: 0.6, fontSize: { xs: 13, md: 14 } }}>
                  A la création d&apos;une procédure d&apos;adoption :
                </Typography>
                <Box component="ul" sx={{ pl: 3, color: '#4B5566', mb: 0, fontSize: { xs: 13, md: 14 } }}>
                  <Box component="li" sx={{ mb: 0.75 }}>
                    Un mail est envoyé à l&apos;adoptant pour lui permettre de régler les frais d&apos;adoption.
                  </Box>
                  <Box component="li">Vous êtes notifié à chaque paiement et les suivez depuis votre espace Petso.</Box>
                </Box>
              </Box>

              <Box>
                <SectionTitle index={3} title="C'est important pour nous 😺" />
                <Typography sx={{ color: '#4B5566', fontSize: { xs: 13, md: 14 } }}>
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    Lors du paiement, l&apos;adoptant peut laisser un pourboire facultatif à Petso.
                  </Box>{' '}
                  Ces pourboires nous permettent de couvrir les frais de paiement et la maintenance, pour continuer à
                  proposer cet outil accessible aux associations !
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            textAlign: 'center',
          }}
        >
          <Box>
            <Box
              component="img"
              alt=""
              src={paymentsFlexImg}
              sx={{ height: 160, width: '100%', objectFit: 'cover', borderRadius: 4, display: 'block' }}
            />
            <Typography sx={{ mt: 1.75, fontSize: '1.2rem', fontWeight: 900, color: '#171F34' }}>
              Offrez plus de flexibilité
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 1.25, color: '#4B5566' }}>
              <Typography sx={{ fontSize: '0.95rem' }}>
                Choisissez comment vos adoptants peuvent régler : paiement en une ou plusieurs fois.
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Box
              component="img"
              alt=""
              src={paymentsFastImg}
              sx={{ height: 160, width: '100%', objectFit: 'cover', borderRadius: 4, display: 'block' }}
            />
            <Typography sx={{ mt: 1.75, fontSize: '1.2rem', fontWeight: 900, color: '#171F34' }}>
              Faites-vous payer plus vite
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 1.25, color: '#4B5566' }}>
              <Typography sx={{ fontSize: '0.95rem' }}>
                Plus besoin d&apos;attendre un chèque ou un virement. Réception rapide et centralisée.
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Box
              component="img"
              alt=""
              src={paymentsTrackImg}
              sx={{ height: 160, width: '100%', objectFit: 'cover', borderRadius: 4, display: 'block' }}
            />
            <Typography sx={{ mt: 1.75, fontSize: '1.2rem', fontWeight: 900, color: '#171F34' }}>
              Suivez les paiements
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 1.25, color: '#4B5566' }}>
              <Typography sx={{ fontSize: '0.95rem' }}>
                Ne courez plus après vos adoptants, suivez les paiements.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.25, justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#7A8197', textAlign: 'center' }}>
          Petso est en partenariat avec Stripe pour assurer des paiements sécurisés
        </Typography>
        <StripeConnectButton
          variant="contained"
          fullWidth={false}
          content="Configurer Stripe Connect"
          sx={{
            textTransform: 'none',
            borderRadius: 3.5,
            fontWeight: 800,
            py: 0.75,
            minHeight: 40,
            boxShadow: '0 8px 18px rgba(32, 56, 100, 0.18)',
            backgroundColor: '#FCB402',
            '&:hover': { backgroundColor: '#E9A900' },
          }}
        />
      </DialogActions>
    </Dialog>
  )
}
